/** API section keys — also used as sectionId throughout the UI */
export const CONFIGURED_SECTION_IDS = {
  personal: "patient_personal_detail",
  finance: "finance_details",
  source: "patient_source",
  addresses: "addresses",
  documents: "documents",
  guarantors: "guarantors",
  contacts: "contacts",
  family: "family_members",
  emergency: "emergency_contacts",
};

export const LIST_SECTION_META = {
  addresses: {
    listKey: "addresses",
    defaultItem: {
      type: "PERMANENT", line1: "", city: "", district: "", state: "", country: "India", pincode: "",
    },
  },
  documents: {
    listKey: "documents",
    defaultItem: { type: "AADHAAR", number: "", issuedBy: "", expiry: "", fileName: "", fileBase64: "" },
  },
  guarantors: {
    listKey: "guarantors",
    defaultItem: { name: "", relation: "", phone: "", idType: "", idValue: "" },
  },
  contacts: {
    listKey: "contacts",
    defaultItem: { channel: "PHONE", type: "PRIMARY", value: "" },
  },
  family_members: {
    listKey: "family",
    defaultItem: { name: "", relation: "", phone: "", nextOfKin: false },
  },
  emergency_contacts: {
    listKey: "emergencyContacts",
    defaultItem: { name: "", relation: "", phone: "", address: "" },
  },
};

/** Maps sourceType → the patient.source key that holds sourceTypeValue */
const SOURCE_TYPE_VALUE_KEYS = {
  HOSPITAL: "hospitalName",
  MARKETING_PERSON: "marketingName",
  EMPLOYEE: "employeeName",
  EXTERNAL_DOCTOR: "referralDoctorId",
};

/** Maps API configuration field_name → frontend state / form field keys */
const API_FIELD_TO_UI_KEY = {
  patient_type: "patientType",
  dateOfBirth: "dob",
  date_of_birth: "dob",
  age_only: "ageOnly",
  marital_status: "maritalStatus",
  blood_group: "bloodGroup",
  identity_marks: "identityMarks",
  family_physician_name: "familyPhysicianName",
  family_physician_phone: "familyPhysicianPhone",
  primary_mobile: "primaryMobile",
  secondary_mobile: "secondaryMobile",
  photo_url: "photoUrl",
  abha_address: "abhaAddress",
  abha_number: "abhaNumber",
  admission_type: "admissionType",
  financial_class: "financialClass",
  employee_id: "employeeId",
  insurance_policy_no: "insurancePolicyNo",
  policy_expiry: "policyExpiry",
  source_type: "sourceType",
  source_type_value: "sourceTypeValue",
  issued_by: "issuedBy",
  file_name: "fileName",
  id_type: "idType",
  id_value: "idValue",
  next_of_kin: "nextOfKin",
};

function toUiFieldKey(apiFieldName) {
  return API_FIELD_TO_UI_KEY[apiFieldName] || apiFieldName;
}

const DEFAULT_SECTION = {
  is_active: true,
  minimum: -1,
  threshold: -1,
  fields: {},
};

function normalizeField(field = {}) {
  return {
    is_active: field.is_active !== false,
    is_mandatory: Boolean(field.is_mandatory),
  };
}

function normalizeSection(section = {}) {
  const fields = {};
  (section.fields || []).forEach((f) => {
    if (f?.field_name) {
      fields[toUiFieldKey(f.field_name)] = normalizeField(f);
    }
  });
  return {
    is_active: section.is_active !== false,
    minimum: section.minimum ?? -1,
    threshold: section.threshold ?? -1,
    fields,
  };
}

export function getDefaultSectionConfiguration() {
  return parseSectionConfiguration([
    {
      patient_source: {
        fields: [
          { is_active: true, field_name: "sourceType", is_mandatory: true },
          { is_active: true, field_name: "sourceTypeValue", is_mandatory: true },
        ],
        minimum: -1, is_active: true, threshold: -1,
      },
      finance_details: {
        fields: [
          { is_active: true, field_name: "admissionType", is_mandatory: true },
          { is_active: true, field_name: "financialClass", is_mandatory: true },
          { is_active: true, field_name: "payer", is_mandatory: false },
          { is_active: true, field_name: "employeeId", is_mandatory: false },
          { is_active: true, field_name: "insurancePolicyNo", is_mandatory: false },
          { is_active: true, field_name: "policyExpiry", is_mandatory: false },
        ],
        minimum: -1, is_active: true, threshold: -1,
      },
      addresses: {
        fields: [
          { is_active: true, field_name: "type", is_mandatory: true },
          { is_active: true, field_name: "line1", is_mandatory: true },
          { is_active: true, field_name: "city", is_mandatory: true },
          { is_active: true, field_name: "district", is_mandatory: false },
          { is_active: true, field_name: "state", is_mandatory: true },
          { is_active: true, field_name: "country", is_mandatory: true },
          { is_active: true, field_name: "pincode", is_mandatory: true },
        ],
        minimum: 1, is_active: true, threshold: 2,
      },
      documents: {
        fields: [
          { is_active: true, field_name: "type", is_mandatory: true },
          { is_active: true, field_name: "number", is_mandatory: true },
          { is_active: true, field_name: "issuedBy", is_mandatory: false },
          { is_active: true, field_name: "expiry", is_mandatory: false },
          { is_active: true, field_name: "fileName", is_mandatory: false },
        ],
        minimum: 1, is_active: true, threshold: 3,
      },
      guarantors: {
        fields: [
          { is_active: true, field_name: "name", is_mandatory: true },
          { is_active: true, field_name: "relation", is_mandatory: true },
          { is_active: true, field_name: "phone", is_mandatory: false },
          { is_active: true, field_name: "idType", is_mandatory: true },
          { is_active: true, field_name: "idValue", is_mandatory: true },
        ],
        minimum: 1, is_active: true, threshold: 2,
      },
      contacts: {
        fields: [
          { is_active: true, field_name: "channel", is_mandatory: true },
          { is_active: true, field_name: "type", is_mandatory: true },
          { is_active: true, field_name: "value", is_mandatory: true },
        ],
        minimum: 1, is_active: true, threshold: 4,
      },
      family_members: {
        fields: [
          { is_active: true, field_name: "name", is_mandatory: true },
          { is_active: true, field_name: "relation", is_mandatory: true },
          { is_active: true, field_name: "phone", is_mandatory: false },
          { is_active: true, field_name: "nextOfKin", is_mandatory: false },
        ],
        minimum: 1, is_active: true, threshold: 3,
      },
      patient_personal_detail: {
        fields: [
          { is_active: true, field_name: "title", is_mandatory: true },
          { is_active: true, field_name: "name", is_mandatory: true },
          { is_active: true, field_name: "patientType", is_mandatory: true },
          { is_active: true, field_name: "gender", is_mandatory: true },
          { is_active: true, field_name: "dob", is_mandatory: true },
          { is_active: true, field_name: "ageOnly", is_mandatory: false },
          { is_active: true, field_name: "maritalStatus", is_mandatory: false },
          { is_active: true, field_name: "bloodGroup", is_mandatory: false },
          { is_active: true, field_name: "occupation", is_mandatory: false },
          { is_active: true, field_name: "nationality", is_mandatory: false },
          { is_active: true, field_name: "religion", is_mandatory: false },
          { is_active: true, field_name: "identityMarks", is_mandatory: false },
          { is_active: true, field_name: "familyPhysicianName", is_mandatory: false },
          { is_active: true, field_name: "familyPhysicianPhone", is_mandatory: false },
          { is_active: true, field_name: "primaryMobile", is_mandatory: true },
          { is_active: true, field_name: "secondaryMobile", is_mandatory: false },
          { is_active: true, field_name: "photoUrl", is_mandatory: false },
          { is_active: true, field_name: "abhaAddress", is_mandatory: false },
          { is_active: true, field_name: "abhaNumber", is_mandatory: false },
        ],
        minimum: -1, is_active: true, threshold: -1,
      },
      emergency_contacts: {
        fields: [
          { is_active: true, field_name: "name", is_mandatory: true },
          { is_active: true, field_name: "relation", is_mandatory: true },
          { is_active: true, field_name: "phone", is_mandatory: true },
          { is_active: true, field_name: "address", is_mandatory: false },
        ],
        minimum: 1, is_active: true, threshold: 2,
      },
    },
  ]);
}

export function parseSectionConfiguration(raw) {
  let payload = raw;
  if (payload?.configuration) {
    payload = payload.configuration;
  }
  const entry = Array.isArray(payload) ? payload[0] : payload;
  if (!entry || typeof entry !== "object") {
    return { sections: {} };
  }

  const sections = {};
  Object.keys(entry).forEach((key) => {
    sections[key] = normalizeSection(entry[key]);
  });
  return { sections };
}

export function getSectionConfig(config, sectionId) {
  if (!config?.sections) return DEFAULT_SECTION;
  return config.sections[sectionId] || DEFAULT_SECTION;
}

export function isSectionActive(config, uiSectionId) {
  const section = getSectionConfig(config, uiSectionId);
  return section.is_active !== false;
}

export function isFieldActive(config, uiSectionId, fieldName) {
  const section = getSectionConfig(config, uiSectionId);
  const field = section.fields[fieldName];
  if (!field) return true;
  return field.is_active !== false;
}

export function isFieldMandatory(config, uiSectionId, fieldName) {
  const section = getSectionConfig(config, uiSectionId);
  const field = section.fields[fieldName];
  if (!field) return false;
  return field.is_active !== false && field.is_mandatory === true;
}

export function getListLimits(config, uiSectionId) {
  const section = getSectionConfig(config, uiSectionId);
  return {
    minimum: section.minimum ?? -1,
    threshold: section.threshold ?? -1,
  };
}

export function canAddListEntry(config, uiSectionId, currentCount) {
  const { threshold } = getListLimits(config, uiSectionId);
  if (threshold < 0) return true;
  return currentCount < threshold;
}

export function canRemoveListEntry(config, uiSectionId, currentCount) {
  const { minimum, threshold } = getListLimits(config, uiSectionId);
  if (minimum < 0 && threshold < 0) return true;
  const min = minimum < 0 ? 0 : minimum;
  return currentCount > min;
}

export function getActiveListFields(config, uiSectionId) {
  const section = getSectionConfig(config, uiSectionId);
  return Object.entries(section.fields)
    .filter(([, f]) => f.is_active !== false)
    .map(([name]) => name);
}

export function getListFieldGridSize(activeCount) {
  if (activeCount <= 0) return 12;
  if (activeCount <= 2) return 6;
  if (activeCount <= 3) return 4;
  if (activeCount <= 4) return 3;
  return 2;
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "boolean") return false;
  return String(value).trim() === "";
}

function validateListSection(patient, config, uiSectionId, listKey) {
  const errors = {};
  const section = getSectionConfig(config, uiSectionId);
  if (!section.is_active) return errors;

  const items = patient[listKey] || [];
  const { minimum } = getListLimits(config, uiSectionId);
  const min = minimum < 0 ? 0 : minimum;

  const filledItems = items.filter((item) =>
    Object.entries(section.fields).some(([fieldName, fieldCfg]) => {
      if (!fieldCfg.is_active) return false;
      return !isEmptyValue(item[fieldName]);
    }));

  if (filledItems.length < min) {
    errors[`${listKey}__min`] = `At least ${min} entr${min === 1 ? "y" : "ies"} required`;
  }

  items.forEach((item, index) => {
    Object.entries(section.fields).forEach(([fieldName, fieldCfg]) => {
      if (!fieldCfg.is_active || !fieldCfg.is_mandatory) return;
      if (isEmptyValue(item[fieldName])) {
        errors[`${listKey}.${index}.${fieldName}`] = "Required";
      }
    });
  });

  return errors;
}

export function validatePatientWithConfig(patient, config) {
  const errors = {};

  if (!config?.sections) {
    return errors;
  }

  const personal = getSectionConfig(config, CONFIGURED_SECTION_IDS.personal);
  if (personal.is_active) {
    Object.entries(personal.fields).forEach(([fieldName, fieldCfg]) => {
      if (!fieldCfg.is_active || !fieldCfg.is_mandatory) return;
      const val = patient[fieldName];
      if (isEmptyValue(val)) {
        errors[fieldName] = "Required";
      }
      if (fieldName === "primaryMobile" && !isEmptyValue(val)) {
        if (!/^\d{10}$/.test(String(val).replace(/\D/g, ""))) {
          errors.primaryMobile = "Enter a valid 10-digit mobile number";
        }
      }
    });
  }

  const finance = getSectionConfig(config, CONFIGURED_SECTION_IDS.finance);
  if (finance.is_active) {
    Object.entries(finance.fields).forEach(([fieldName, fieldCfg]) => {
      if (!fieldCfg.is_active || !fieldCfg.is_mandatory) return;
      const val = patient.encounter?.[fieldName];
      if (isEmptyValue(val)) {
        errors[fieldName] = "Required";
      }
    });
  }

  const source = getSectionConfig(config, CONFIGURED_SECTION_IDS.source);
  if (source.is_active) {
    if (source.fields.sourceType?.is_active && source.fields.sourceType?.is_mandatory) {
      if (isEmptyValue(patient.source?.sourceType)) {
        errors.sourceType = "Required";
      }
    }
    if (source.fields.sourceTypeValue?.is_active && source.fields.sourceTypeValue?.is_mandatory) {
      const type = patient.source?.sourceType;
      const valueKey = SOURCE_TYPE_VALUE_KEYS[type];
      if (valueKey && isEmptyValue(patient.source?.[valueKey])) {
        errors.sourceTypeValue = "Required";
      }
    }
  }

  Object.assign(errors, validateListSection(patient, config, CONFIGURED_SECTION_IDS.documents, "documents"));
  Object.assign(errors, validateListSection(patient, config, CONFIGURED_SECTION_IDS.contacts, "contacts"));
  Object.assign(errors, validateListSection(patient, config, CONFIGURED_SECTION_IDS.addresses, "addresses"));
  Object.assign(errors, validateListSection(patient, config, CONFIGURED_SECTION_IDS.family, "family"));
  Object.assign(errors, validateListSection(patient, config, CONFIGURED_SECTION_IDS.emergency, "emergencyContacts"));
  Object.assign(errors, validateListSection(patient, config, CONFIGURED_SECTION_IDS.guarantors, "guarantors"));

  if (!patient.encounter?.admittingDoctorId) {
    errors.admittingDoctor = "Admitting doctor is required";
  }

  if (!patient.encounter?.bedId) {
    errors.bedId = "Bed selection is required";
  }

  return errors;
}

export function ensureMinimumListEntries(patient, config) {
  if (!config?.sections) return patient;

  let changed = false;
  const next = { ...patient };
  Object.entries(LIST_SECTION_META).forEach(([apiKey, meta]) => {
    const section = config.sections[apiKey];
    if (!section?.is_active) return;

    const { minimum } = section;
    const min = minimum < 0 ? 0 : minimum;
    const current = [...(next[meta.listKey] || [])];
    const before = current.length;
    while (current.length < min) {
      current.push({ ...meta.defaultItem });
    }
    if (current.length !== before) {
      changed = true;
      next[meta.listKey] = current;
    }
  });
  return changed ? next : patient;
}

/** UI-only section ids (not from API config) */
export const UI_SECTION_IDS = {
  admission: "admission",
  bedAssignment: "bed-assignment",
};

const LIST_KEY_TO_SECTION = {
  documents: CONFIGURED_SECTION_IDS.documents,
  contacts: CONFIGURED_SECTION_IDS.contacts,
  addresses: CONFIGURED_SECTION_IDS.addresses,
  family: CONFIGURED_SECTION_IDS.family,
  emergencyContacts: CONFIGURED_SECTION_IDS.emergency,
  guarantors: CONFIGURED_SECTION_IDS.guarantors,
};

const FALLBACK_FINANCE_KEYS = new Set([
  "admissionType",
  "financialClass",
  "payer",
  "employeeId",
  "insurancePolicyNo",
  "policyExpiry",
]);

const FALLBACK_PERSONAL_KEYS = new Set([
  "name",
  "gender",
  "dob",
  "primaryMobile",
  "patientType",
]);

function getSectionFieldKeys(config, sectionId) {
  const section = getSectionConfig(config, sectionId);
  return new Set(Object.keys(section.fields));
}

/** Maps validation error keys to accordion section ids for collapsed-section highlighting. */
export function mapErrorsToSectionIds(errors, config) {
  const sectionIds = new Set();
  if (!errors || typeof errors !== "object") return sectionIds;

  const personalKeys = config?.sections
    ? getSectionFieldKeys(config, CONFIGURED_SECTION_IDS.personal)
    : FALLBACK_PERSONAL_KEYS;
  const financeKeys = config?.sections
    ? getSectionFieldKeys(config, CONFIGURED_SECTION_IDS.finance)
    : FALLBACK_FINANCE_KEYS;

  Object.keys(errors).forEach((key) => {
    const listPrefix = key.match(/^([a-zA-Z]+)(?:\.|__)/);
    if (listPrefix) {
      const sectionId = LIST_KEY_TO_SECTION[listPrefix[1]];
      if (sectionId) {
        sectionIds.add(sectionId);
        return;
      }
    }

    if (key === "admittingDoctor") {
      sectionIds.add(UI_SECTION_IDS.admission);
      return;
    }

    if (key === "bedId") {
      sectionIds.add(UI_SECTION_IDS.bedAssignment);
      return;
    }

    if (key === "sourceType" || key === "sourceTypeValue") {
      sectionIds.add(CONFIGURED_SECTION_IDS.source);
      return;
    }

    if (financeKeys.has(key)) {
      sectionIds.add(CONFIGURED_SECTION_IDS.finance);
      return;
    }

    if (personalKeys.has(key)) {
      sectionIds.add(CONFIGURED_SECTION_IDS.personal);
    }
  });

  return sectionIds;
}

export { SOURCE_TYPE_VALUE_KEYS };
